// src/contexts/SocketContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [connected, setConnected] = useState(false);

  const normalizeId = (value) => String(value || '');

  useEffect(() => {
    if (!user?._id) {
      // Disconnect if no user
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // Initialize socket connection
    const newSocket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('[Socket.io] Connected:', newSocket.id);
      setConnected(true);
      
      // Join user's room
      newSocket.emit('user:join', normalizeId(user._id));
    });

    newSocket.on('disconnect', () => {
      console.log('[Socket.io] Disconnected');
      setConnected(false);
      setOnlineUsers(new Set());
    });

    newSocket.on('presence:snapshot', (onlineUserIds) => {
      if (!Array.isArray(onlineUserIds)) return;
      setOnlineUsers(new Set(onlineUserIds.map((id) => normalizeId(id)).filter(Boolean)));
    });

    newSocket.on('user:online', (userId) => {
      const normalized = normalizeId(userId);
      if (!normalized) return;
      setOnlineUsers(prev => new Set([...prev, normalized]));
    });

    newSocket.on('user:offline', (userId) => {
      const normalized = normalizeId(userId);
      if (!normalized) return;
      setOnlineUsers(prev => {
        const updated = new Set(prev);
        updated.delete(normalized);
        return updated;
      });
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Socket.io] Connection error:', error.message);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user?._id]);

  const emitTyping = (recipientId, isTyping) => {
    if (socket && connected) {
      socket.emit(isTyping ? 'typing:start' : 'typing:stop', { recipientId });
    }
  };

  return (
    <SocketContext.Provider value={{ 
      socket, 
      connected, 
      onlineUsers,
      emitTyping,
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};
